import {getLocalStorageItem, setLocalStorageItem} from '../localStorage';
import {once} from 'lodash';
import amplitude from 'amplitude-js';
import appboy from 'appboy-web-sdk/appboy.core.min';
import fetchUser from './fetchUser';
import qs from 'qs';

function isOptedOut(user: User) {
    return user.isAuthenticated && !user.webAnalyticsEnabled;
}

const initialize = once(async () => {
    const user = await fetchUser();

    const ga = (window as any).ga;
    const Raven = (window as any).Raven;
    const fbq = (window as any).fbq;

    if (isOptedOut(user)) return;

    const amplitudeInstance = amplitude.getInstance();

    // Monkeypatch amplitude's storage layer to ensure it does not use cookies.
    // Cookies which change on every request prevent HTTP caching.
    // @ts-ignore
    amplitudeInstance.cookieStorage = {
        reset: () => {},
        options: () => ({}),
        get: (name: string) => getLocalStorageItem<any>(name, undefined),
        set: (name: string, value: any) => setLocalStorageItem<any>(name, value),
        remove: (name: string) => setLocalStorageItem<any>(name, undefined),
    };

    amplitudeInstance.init(Gaia.amplitudeApiKey, undefined, {
        includeReferrer: true,
        includeUtm: true,
        includeGclid: true
    });

    appboy.initialize(Gaia.blazeSdkKey, {
        baseUrl: 'https://customer.iad-03.braze.com/api/v3',
        enableLogging: false,
        // Use localStorage instead of cookies.
        // Cookies which change on every request prevent HTTP caching.
        noCookies: true,
    });
    appboy.openSession();

    // This is exclusively for the legacy angular code. Do not use otherwise.
    (window as any).appboy = appboy;
    (window as any).amplitude = amplitude;

    fbq('init', '1304750436225011', user.email ? {em: user.email} : undefined);
    fbq('track', 'PageView');

    ga('create', Gaia.googleTrackingKey, 'auto');
    ga('send', 'pageview');

    amplitude.getInstance().setUserId(user.id);
    ga('set', 'userId', user.id);
    appboy.changeUser(user.id);

    // Do not enable Raven in debug mode because it captures exceptions and
    // looses their callstack.
    if (!Gaia.isDebug) {
        Raven.config('https://eebe259ebaa846d39aaae0e3404505ab@sentry.io/1238795').install();
    }

    // Store referrer & landing page
    if (
        !user.isAuthenticated &&
        document.cookie.search('referrer') < 0 &&
        document.cookie.search('landing_page') < 0
    ) {

        // Store referrer only the first time a user hits the website + landing page
        document.cookie = 'referrer=' + document.referrer + ';path=/';
        document.cookie = 'landing_page=' + location.href + ';path=/';
        trackAnalytics('Set Landing Page', {page: location.href});
    }
});

initialize();

export default async function trackAnalytics(
    name: string,
    properties?: Record<string, any>,
    hitCallback?: () => void
) {
    await initialize();
    const ga = (window as any).ga;

    if (isOptedOut(await fetchUser())) return;

    // Amplitude complains about empty label property
    if (properties && (properties.label === null || properties.label === '')) {
        delete properties['label'];
    }

    amplitude.getInstance().logEvent(name, properties);

    // track for Braze
    appboy.logCustomEvent(name, properties);

    // track for Google Analytics
    if (properties) {
        ga('send', 'event', {
            eventAction: name,
            eventCategory: properties.category,
            eventLabel: properties.label,
            eventValue: properties.value,
            hitCallback
        });
    }
}

export async function trackRevenueAnalytics(
    name: string,
    properties?: Record<string, any>
) {
    await initialize();
    const {upgrade} = qs.parse(window.location.search);

    if (isOptedOut(await fetchUser())) return;

    if (properties && properties.revenue && properties.productId) {
        const revenue = new amplitude.Revenue().setProductId(properties.productId)
            .setPrice(properties.revenue).setQuantity(1);

        //log amplitude
        amplitude.getInstance().logRevenueV2(revenue);

        //log braze
        let plan = properties.plan;
        if (upgrade === 'True') {
            /*True is a string in the URL.
            Upgrade is either True or False*/
            plan = plan + '_upgrade';
        }
        appboy.logPurchase(plan, properties.revenue, properties.currencyCode, 1, {upgrade:
            upgrade});
    }
    trackAnalytics(name, properties);
}

// See https://developers.google.com/analytics/devguides/collection/analyticsjs/sending-hits
export async function trackFormAnalytics(formId: string, name: string, category: string) {
    if (isOptedOut(await fetchUser())) return;

    const form = document.getElementById(formId) as HTMLFormElement | null;
    if (!form) return;

    form.addEventListener('submit', event => {
        event.preventDefault();

        setTimeout(submitForm, 1000);
        let formSubmitted = false;

        function submitForm() {
            if (!formSubmitted && form) {
                formSubmitted = true;
                form.submit();
            }
        }

        trackAnalytics(name, {category}, submitForm);
    });
}
